import { z } from 'zod';
import {
  addressSchema,
  createModel,
  imageWithUploadSchema,
  partySchema,
  QueryMany,
  QueryOne,
} from '../../common';
import { nameSchema } from '../../validation';
import { savedLocationContactSchema } from './saved-location.contact.model';
import { savedLocationClientSchema } from './saved-location.client.model';
import { UTagSharedSchema } from '../utag';
/**
 * `location db model`
 *
 * these are saved locations that can belong to a Client (third-party
 * counterparty) or be the org's own branch.
 */
export const SavedLocationSchema = createModel({
  /** a list of images for the place.
   *
   * If no image is provided then, this will be empty array [].
   */
  images: z.array(imageWithUploadSchema),
  /** a display name for the location such as 'Home', 'Office', etc. */
  name: nameSchema,

  /** the address details of the saved location */
  address: addressSchema,

  /**
   * The owning Org as a relations-v2 {@link partySchema} snapshot
   * (carries `roles[]` + `ids[]` + normalized `target: 'org'`). Replaces
   * the legacy `relationSchema` shape — see migration
   * `<ts>-relations-v2-saved-location.ts` Phase A'.
   */
  org: partySchema,

  /** the Client that this location belongs to.
   * if empty, then it means it's own branch.
   */
  client: savedLocationClientSchema.optional(),

  /** is this a branch of the org */
  isOwnBranch: z.boolean(),

  /**
   * Optional Google Maps share URL kept as a reference when the user
   * can't pin the exact spot on the map. Stored verbatim so the
   * backend `resolveGoogleUrl` endpoint can be re-run later if the
   * URL format changes.
   */
  locationUrl: z.string().url().optional(),

  /**
   * `false` when the user marked the pin as approximate (couldn't drop
   * the exact spot — e.g. a vague "Sulay neighborhood" pick). Mirrors
   * the order destination `isAccurateLocation` flag so dispatch can
   * tell at a glance which stops still need a precise drop. Defaults
   * to `true` (existing rows are treated as exact); the publish flow
   * and the location form let the user flip it explicitly.
   */
  isAccurate: z.boolean().default(true),

  /**
   * the list of contacts for this location. Each contact carries a
   * `Contact._id` and at most one is marked primary.
   *
   * May be **empty**: a place is often worth keeping before anyone has a
   * name for the gate — an agent saving a pin from a WhatsApp link, or a
   * warehouse with no single person to call. This mirrors
   * `OrderDestination.contacts` and the `contacts` field on
   * `savedLocationCreateSchemaAPI`, which both document empty as valid.
   * The dashboard form still requires the user to pick one (its own
   * `superRefine`) — that is a form rule, not a storage rule.
   */
  contacts: z.array(savedLocationContactSchema),
  UTags: z.array(UTagSharedSchema),
});

export type SavedLocation = z.infer<typeof SavedLocationSchema.model>;
export type SavedLocationData = z.infer<typeof SavedLocationSchema.data>;
export type SavedLocationPartial = z.infer<typeof SavedLocationSchema.partial>;
export type SavedLocationQueryOne = QueryOne<SavedLocation>;
export type SavedLocationQueryMany = QueryMany<SavedLocation>;
